import { fromJS, List, Record } from 'immutable';
import {
  generateKeys,
  generateSchema,
  setInModel,
} from '../../../../utils/helpers/model.helper';
import { EmailDefaults } from './email-defaults.model';
import { PaymentReminders } from './payment-reminders.model';
import { PaymentsSettings } from './payments.model';
import { TaxYearStart } from './tax-year-start.model';
import { Tax } from './tax.model';
import { WithholdingTax } from './withholding-tax.model';
import { DepositToggle } from './deposit-toggle.model';

export enum ManualPaymentReceiptsEnabledEnum {
  never_toggled = 'never_toggled',
  on = 'on',
  off = 'off',
}

export const COMPANY_SETTINGS_SCHEMA = Object.freeze({
  COUNTRY: 'US',
  COMMUNICATION_LOCALE: 'en-US',
  TAX_YEAR_START: TaxYearStart,
  TAXES: List<Tax>([]),
  WITHHOLDING_TAX: WithholdingTax,
  EMAIL_DEFAULTS: EmailDefaults,
  PAYMENTS: PaymentsSettings,
  PAYMENT_REMINDERS: PaymentReminders,
  MANUAL_PAYMENT_RECEIPTS_ENABLED:
    ManualPaymentReceiptsEnabledEnum.never_toggled,
  PRODUCT_NAME_ENABLED: false,
  INVOICE_UNOPENED_REMINDERS_ENABLED: true,
  DEPOSIT_TOGGLE: DepositToggle,
  SAVE_ITEMS: true,
  MFA_ENABLED: false,
  PROFILE_ENABLED: false,
});
const RECORD = generateSchema(COMPANY_SETTINGS_SCHEMA);
const KEYS: any = generateKeys(COMPANY_SETTINGS_SCHEMA);

export class CompanySettings extends Record(RECORD) {
  static KEYS: any = KEYS;

  constructor(props?: any) {
    super(props && props.toMap ? props.toMap() : fromJS(props || {}));

    const selfModel = setInModel(this, [
      {
        key: KEYS.TAX_YEAR_START,
        value: new TaxYearStart(this.taxYearStart),
      },
      {
        key: KEYS.TAXES,
        value: List(this.taxes).map((params) => new Tax(params)),
      },
      {
        key: KEYS.WITHHOLDING_TAX,
        value: new WithholdingTax(this.withholdingTax),
      },
      {
        key: KEYS.EMAIL_DEFAULTS,
        value: new EmailDefaults(this.emailDefaults),
      },
      {
        key: KEYS.PAYMENTS,
        value: new PaymentsSettings(this.payments),
      },
      {
        key: KEYS.PAYMENT_REMINDERS,
        value: new PaymentReminders(this.paymentReminders),
      },
      {
        key: KEYS.DEPOSIT_TOGGLE,
        value: new DepositToggle(this.depositToggle),
      },
    ]);

    return selfModel as CompanySettings;
  }

  get country(): string {
    return this.get(KEYS.COUNTRY);
  }

  get taxYearStart(): TaxYearStart {
    return this.get(KEYS.TAX_YEAR_START);
  }

  get taxYearStartMonth(): number {
    return this.taxYearStart.month;
  }

  get taxYearStartDay(): number {
    return this.taxYearStart.day;
  }

  get taxes(): List<Tax> {
    return this.get(KEYS.TAXES);
  }

  get withholdingTax(): WithholdingTax {
    return this.get(KEYS.WITHHOLDING_TAX);
  }

  get emailDefaults(): EmailDefaults {
    return this.get(KEYS.EMAIL_DEFAULTS);
  }

  get payments(): PaymentsSettings {
    return this.get(KEYS.PAYMENTS);
  }

  get paymentReminders(): PaymentReminders {
    return this.get(KEYS.PAYMENT_REMINDERS);
  }

  get manualPaymentReceiptsEnabled(): ManualPaymentReceiptsEnabledEnum {
    return this.get(KEYS.MANUAL_PAYMENT_RECEIPTS_ENABLED);
  }

  get productNameEnabled(): boolean {
    return this.get(KEYS.PRODUCT_NAME_ENABLED);
  }

  get invoiceUnopenedRemindersEnabled(): boolean {
    return this.get(KEYS.INVOICE_UNOPENED_REMINDERS_ENABLED);
  }

  get depositToggle(): DepositToggle {
    return this.get(KEYS.DEPOSIT_TOGGLE);
  }

  get saveItems(): boolean {
    return this.get(KEYS.SAVE_ITEMS);
  }

  get mfaEnabled(): boolean {
    return this.get(KEYS.MFA_ENABLED);
  }

  get profileEnabled(): boolean {
    return this.get(KEYS.PROFILE_ENABLED);
  }
}
